/**
 * The one place a stacking layer is decided.
 *
 * Stylesheets already read this scale as the `--z-index-*` custom properties in
 * `src/style.css`; this module is the same scale for the code that cannot use a custom
 * property — anything handing a *number* to a library (tippy's `zIndex`) or writing an
 * inline `:style`. Reach for a token here before inventing a literal at a call site.
 *
 * The two lists must not drift, so `zIndex.test.ts` parses `style.css` and fails if a layer
 * exists on one side only or the values disagree. A new layer is therefore added twice, on
 * purpose: once as a custom property, once here.
 *
 * `--z-index-<kebab-name>` maps to `<camelName>`, digits included — `--z-index-layer-2000`
 * is `layer2000`.
 *
 * The `layer<N>` entries are unnamed rungs inherited from the original scale. Prefer a token
 * that says what it is; only add another numbered rung when nothing describes the layer.
 */
export const Z_INDEX = {
  base: 0,
  layer1: 1,
  layer2: 2,
  layer5: 5,
  layer6: 6,
  contentOverlay: 10,
  layer12: 12,
  layer15: 15,
  layer20: 20,
  layer21: 21,
  layer22: 22,
  floatingBadge: 30,
  sidebarOverlay: 40,
  bottomNavigation: 50,
  stickyHeader: 60,
  layer70: 70,
  layer100: 100,
  modalOverlay: 1000,
  modalInlineOverlay: 1001,
  modalOverlayElevated: 1100,
  layer2000: 2000,
  layer2500: 2500,
  drawerLayer: 3000,
  navDrawerLayer: 3100,
  navDrawerOpenNavbar: 3200,
  appOverlay: 9999,
  toast: 10_000,
  tooltip: 10_050,
} as const;

export type TZIndexLayer = keyof typeof Z_INDEX;
